import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import type { ModuleInfoDto } from '../../types/subscriptions';
import { getCategoryLabel, getCategoryColor } from '../../utils/subscriptions';
import { useState } from 'react';

interface CascadeDisableDialogProps {
  open: boolean;
  onClose: () => void;
  module: ModuleInfoDto;
  affectedModules: ModuleInfoDto[];
  willCascadeDisable: ModuleInfoDto[];
  onConfirm: (force: boolean) => void;
  isLoading?: boolean;
}

/**
 * Dialog shown when a user tries to disable a module that other modules depend on.
 * Warns about affected modules and allows force cascade disable.
 */
export const CascadeDisableDialog = ({
  open,
  onClose,
  module,
  affectedModules,
  willCascadeDisable,
  onConfirm,
  isLoading = false,
}: CascadeDisableDialogProps) => {
  const [forceCascade, setForceCascade] = useState(false);

  const handleConfirm = () => {
    onConfirm(forceCascade);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      setForceCascade(false); // Reset to default
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ErrorOutlineIcon />
          <Typography variant="h6">Warning: Dependent Modules Active</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Module being disabled */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              You're trying to disable:
            </Typography>
            <Card variant="outlined" sx={{ bgcolor: 'error.lighter', borderColor: 'error.main' }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {module.name}
                  </Typography>
                  <Chip
                    label={getCategoryLabel(module.category)}
                    size="small"
                    sx={{
                      bgcolor: getCategoryColor(module.category),
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {module.description}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Warning about dependent modules */}
          <Alert severity="error" icon={<WarningIcon />}>
            <Typography variant="body2" fontWeight={600}>
              Cannot disable this module!
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {affectedModules.length} {affectedModules.length === 1 ? 'module depends' : 'modules depend'} on{' '}
              <strong>{module.name}</strong>. Disabling it will break their functionality.
            </Typography>
          </Alert>

          {/* List of affected modules */}
          <Box>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Affected Modules:
            </Typography>
            <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
              <List dense sx={{ maxHeight: 250, overflowY: 'auto' }}>
                {affectedModules.map((dep, index) => (
                  <div key={dep.id}>
                    {index > 0 && <Divider />}
                    <ListItem>
                      <ListItemIcon>
                        <LinkOffIcon color="error" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2" fontWeight={600}>
                              {dep.name}
                            </Typography>
                            <Chip
                              label={getCategoryLabel(dep.category)}
                              size="small"
                              sx={{
                                bgcolor: getCategoryColor(dep.category),
                                color: 'white',
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </Stack>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {dep.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </div>
                ))}
              </List>
            </Card>
          </Box>

          <Divider />

          {/* Force cascade option */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceCascade}
                  onChange={(e) => setForceCascade(e.target.checked)}
                  disabled={isLoading}
                  color="error"
                />
              }
              label={
                <Typography variant="body2" color="error">
                  Force cascade disable {willCascadeDisable.length}{' '}
                  {willCascadeDisable.length === 1 ? 'module' : 'modules'}
                </Typography>
              }
            />

            {forceCascade ? (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Stack spacing={1}>
                  <Typography variant="body2" fontWeight={600}>
                    ⚠️ This will disable the following modules:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {[module, ...willCascadeDisable].map((mod) => (
                      <Chip
                        key={mod.id}
                        label={mod.name}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="error.dark">
                    This action may break features and functionality for this organization.
                  </Typography>
                </Stack>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  To disable <strong>{module.name}</strong>, you must first disable the modules that depend on it,
                  or check the box above to force cascade disable all affected modules.
                </Typography>
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: forceCascade ? 'error.lighter' : 'background.default' }}>
        <Button onClick={handleClose} disabled={isLoading} variant="outlined">
          Cancel
        </Button>
        <Button
          variant="contained"
          color={forceCascade ? 'error' : 'inherit'}
          onClick={handleConfirm}
          disabled={isLoading || !forceCascade}
          startIcon={forceCascade ? <WarningIcon /> : undefined}
        >
          {forceCascade
            ? `Force Disable ${willCascadeDisable.length + 1} Modules`
            : 'Disable Not Allowed'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
